import { useState, useEffect } from 'react'
import { VITE_API_BASE } from '../config/apiConfig'
import { Table, Tag, Button, Modal, Input, Space, message, Card, Divider, Select } from 'antd'
import { CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons'

const { TextArea } = Input

export default function InstitutionApplicationsPage() {
  const API = VITE_API_BASE
  const [apps, setApps] = useState([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('pending')
  const [selected, setSelected] = useState(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => { loadApps() }, [statusFilter])

  const loadApps = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API}/institution-applications?status=${statusFilter}`)
      const json = await res.json()
      if (json.success) setApps(json.data)
    } catch(e) { message.error('加载失败') }
    setLoading(false)
  }

  const openDetail = (app) => { setSelected(app); setDetailOpen(true); setRejectReason(app.reject_reason || '') }

  const handleApprove = async () => {
    setActionLoading(true)
    try {
      const res = await fetch(`${API}/institution-applications/${selected.id}/review`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'approve' })
      })
      const json = await res.json()
      if (json.success) {
        message.success('已通过！' + (json.data?.message || ''))
        setDetailOpen(false); loadApps()
      } else { message.error(json.error || '操作失败') }
    } catch(e) { message.error('网络错误') }
    setActionLoading(false)
  }

  const handleReject = async () => {
    if (!rejectReason.trim()) { message.warning('请填写拒绝原因'); return }
    setActionLoading(true)
    try {
      const res = await fetch(`${API}/institution-applications/${selected.id}/review`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'reject', reject_reason: rejectReason })
      })
      const json = await res.json()
      if (json.success) { message.success('已拒绝'); setDetailOpen(false); loadApps() }
      else { message.error(json.error || '操作失败') }
    } catch(e) { message.error('网络错误') }
    setActionLoading(false)
  }

  const typeMap = { kindergarten: '幼儿园', training: '培训机构', community: '社区中心', base: '活动基地', school: '中小学', other: '其他' }
  const statusMap = { pending: '待审核', approved: '已通过', rejected: '已拒绝' }
  const statusColor = { pending: 'orange', approved: 'green', rejected: 'red' }

  const cols = [
    { title: '编号', dataIndex: 'id', width: 120, render: v => <code style={{fontSize:12}}>{v}</code> },
    { title: '机构名称', dataIndex: 'name', render: v => <strong>{v}</strong> },
    { title: '联系人', dataIndex: 'contact_name', width: 90 },
    { title: '手机号', dataIndex: 'phone', width: 120, render: v => <span style={{color:'#667eea'}}>{v}</span> },
    { title: '类型', dataIndex: 'type', width: 90, render: v => typeMap[v] || v },
    { title: '状态', dataIndex: 'status', width: 90, render: v => <Tag color={statusColor[v]}>{statusMap[v]}</Tag> },
    { title: '申请时间', dataIndex: 'created_at', width: 160, render: v => v ? new Date(v).toLocaleString('zh-CN') : '—' },
    { title: '操作', width: 100, render: (_, r) => r.status === 'pending' ? (
      <Button size="small" type="primary" icon={<EyeOutlined />} onClick={() => openDetail(r)}>审核</Button>
    ) : <Button size="small" onClick={() => openDetail(r)}>查看</Button> },
  ]

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 20, margin: 0 }}>🏫 机构入驻审核</h2>
        <Space>
          <Select value={statusFilter} onChange={v => setStatusFilter(v)} style={{ width: 130 }}>
            <Select.Option value="pending">待审核 ({apps.length})</Select.Option>
            <Select.Option value="approved">已通过</Select.Option>
            <Select.Option value="rejected">已拒绝</Select.Option>
            <Select.Option value="">全部</Select.Option>
          </Select>
        </Space>
      </div>

      <Card>
        <Table columns={cols} dataSource={apps} rowKey="id" loading={loading} pagination={{ pageSize: 15 }} />
      </Card>

      <Modal
        title={`审核申请 — ${selected?.name || ''}`}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={520}
      >
        {selected && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{ padding: '10px 12px', background: '#f5f5f5', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>机构名称</div>
                <div style={{ fontWeight: 600 }}>{selected.name}</div>
              </div>
              <div style={{ padding: '10px 12px', background: '#f5f5f5', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>机构类型</div>
                <div style={{ fontWeight: 600 }}>{typeMap[selected.type] || selected.type}</div>
              </div>
              <div style={{ padding: '10px 12px', background: '#f5f5f5', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>联系人</div>
                <div style={{ fontWeight: 600 }}>{selected.contact_name || '—'}</div>
              </div>
              <div style={{ padding: '10px 12px', background: '#f5f5f5', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>手机号</div>
                <div style={{ fontWeight: 600, color: '#667eea' }}>{selected.phone}</div>
              </div>
              <div style={{ padding: '10px 12px', background: '#f5f5f5', borderRadius: 8, gridColumn: '1/-1' }}>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>机构地址</div>
                <div>{selected.address || '—'}</div>
              </div>
              {selected.description && (
                <div style={{ padding: '10px 12px', background: '#f5f5f5', borderRadius: 8, gridColumn: '1/-1' }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>机构简介</div>
                  <div>{selected.description}</div>
                </div>
              )}
              {selected.status === 'rejected' && selected.reject_reason && (
                <div style={{ padding: '10px 12px', background: '#fff2f0', borderRadius: 8, gridColumn: '1/-1', border: '1px solid #ffccc7' }}>
                  <div style={{ fontSize: 12, color: '#ff4d4f', marginBottom: 4 }}>拒绝原因</div>
                  <div>{selected.reject_reason}</div>
                </div>
              )}
            </div>
            <Divider />
            {selected.status === 'pending' ? (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>拒绝原因（选填）</div>
                  <TextArea rows={2} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="如不通过请填写原因" maxLength={100} />
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <Button danger onClick={handleReject} loading={actionLoading} icon={<CloseOutlined />}>拒绝</Button>
                  <Button type="primary" onClick={handleApprove} loading={actionLoading} icon={<CheckOutlined />} style={{ background: '#52c41a', borderColor: '#52c41a' }}>通过并开通账号</Button>
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: '#999', textAlign: 'right' }}>
                  通过后自动创建账号，初始密码：inst + 手机号后6位
                </div>
              </div>
            ) : <div style={{ textAlign: 'right' }}><Button onClick={() => setDetailOpen(false)}>关闭</Button></div>}
          </div>
        )}
      </Modal>
    </div>
  )
}
